/**
 * MenuUtils - Pure utility functions for menu operations
 * Provides reusable functions for offcanvas, menu animations, ARIA management, and more
 */

export const MenuUtils = {
  /**
   * Sets up offcanvas event listeners with callbacks
   */
  setupOffcanvasListeners(offcanvasElement, callbacks) {
    if (!offcanvasElement) return;

    const { onShow, onHide, onHidden } = callbacks;

    if (onShow) offcanvasElement.addEventListener('show.bs.offcanvas', onShow);
    if (onHide) offcanvasElement.addEventListener('hide.bs.offcanvas', onHide);
    if (onHidden) offcanvasElement.addEventListener('hidden.bs.offcanvas', onHidden);
  },

  /**
   * Sets up common menu button listeners (close, back)
   */
  setupMenuButtons(container, callbacks) {
    const { onClose, onBack } = callbacks;

    const closeBtn = container.querySelector('.menu-close-btn');
    const backBtn = container.querySelector('.menu-back-btn');

    if (closeBtn && onClose) {
      closeBtn.addEventListener('click', onClose);
    }

    if (backBtn && onBack) {
      backBtn.addEventListener('click', onBack);
    }
  },

  /**
   * Sets up keyboard navigation (Escape key)
   */
  setupKeyboardNav(element, onEscape) {
    if (!element || !onEscape) return;

    element.addEventListener('keydown', (e) => {
      if (e.code === 'Escape') onEscape();
    });
  },

  /**
   * Shows/hides menu with animation classes
   */
  toggleMenu(menu, show = true) {
    if (show) {
      menu.classList.remove('d-none');
      menu.classList.add('visible');
      menu.classList.add('show');
    } else {
      menu.classList.remove('show');
      setTimeout(() => {
        menu.classList.remove('visible');
      }, 200);
    }
  },

  /**
   * Shows/hides level3 menu (includes d-none management)
   */
  toggleLevel3Menu(menu, show = true) {
    if (show) {
      menu.classList.remove('d-none');
      menu.classList.add('visible');
      menu.classList.add('show');
    } else {
      menu.classList.remove('show');
      setTimeout(() => {
        menu.classList.remove('visible');
        menu.classList.add('d-none');
      }, 200);
    }
  },

  /**
   * Shows/hides region selector level3 menu (no d-none management)
   */
  toggleRegionLevel3Menu(menu, show = true) {
    if (show) {
      menu.classList.add('visible');
      menu.classList.add('show');
    } else {
      menu.classList.remove('show');
      setTimeout(() => {
        menu.classList.remove('visible');
      }, 200);
    }
  },

  /**
   * Manages aria-expanded attribute
   */
  setAriaExpanded(triggerButton, expanded) {
    if (triggerButton) {
      triggerButton.setAttribute('aria-expanded', expanded.toString());
    }
  },

  /**
   * Finds trigger button for a menu
   */
  findTriggerButton(container, menuId) {
    return container.querySelector(`[aria-controls="${menuId}"]`);
  },

  /**
   * Sets focus and resets aria-expanded for menu closing
   */
  handleMenuClose(container, menuId, shouldFocus = true) {
    const triggerButton = MenuUtils.findTriggerButton(container, menuId);
    if (triggerButton) {
      MenuUtils.setAriaExpanded(triggerButton, false);
      if (shouldFocus) triggerButton.focus();
    }
  },

  /**
   * Focus management utilities
   */
  focus: {
    /**
     * Focus on first close button in menu
     */
    closeButton(menu) {
      const closeBtn = menu.querySelector('.menu-close-btn');
      if (closeBtn) closeBtn.focus();
    },

    /**
     * Focus on specific element if it exists
     */
    element(element) {
      if (element && typeof element.focus === 'function') {
        element.focus();
      }
    },

    /**
     * Focus on element by selector within container
     */
    selector(container, selector) {
      const element = container.querySelector(selector);
      MenuUtils.focus.element(element);
    },
  },

  /**
   * Manages body scroll and backdrop
   */
  toggleBodyScroll(disable) {
    const value = disable ? 'hidden' : '';
    document.body.style.overflow = value;
    setTimeout(() => {
      document.body.style.overflow = value;
    }, 500);
  },

  /**
   * Generic function to hide all menus of a specific type
   */
  hideMenus(container, menuSelector, resetAriaCallback) {
    if (!container) return;

    const menus = container.querySelectorAll(menuSelector);
    menus.forEach((menu) => {
      if (menu.classList.contains('show')) {
        // Use appropriate toggle method based on container
        if (container.id === 'region-selector' && menuSelector.includes('level3')) {
          MenuUtils.toggleRegionLevel3Menu(menu, false);
        } else if (menuSelector.includes('level3')) {
          MenuUtils.toggleLevel3Menu(menu, false);
        } else {
          MenuUtils.toggleMenu(menu, false);
        }

        if (resetAriaCallback) {
          resetAriaCallback(menu);
        }
      }
    });
  },

  /**
   * Generic offcanvas manager
   */
  createOffcanvasManager(element, callbacks) {
    const { onShow, onHide, onClose } = callbacks;

    return {
      show() {
        const instance = window.bootstrap.Offcanvas.getInstance(element);
        if (instance) instance.show();
      },

      hide() {
        const instance = window.bootstrap.Offcanvas.getInstance(element);
        if (instance) instance.hide();
      },

      setupListeners() {
        MenuUtils.setupOffcanvasListeners(element, { onShow, onHide });
        if (onClose) {
          MenuUtils.setupKeyboardNav(element, onClose);
        }
      },
    };
  },

  /**
   * Backdrop utilities
   */
  backdrop: {
    show(element) {
      if (element && !element.classList.contains('show')) {
        element.classList.add('show');
      }
    },

    hide(element) {
      if (element && element.classList.contains('show')) {
        element.classList.remove('show');
      }
    },

    toggle(element, show) {
      if (show) {
        MenuUtils.backdrop.show(element);
      } else {
        MenuUtils.backdrop.hide(element);
      }
    },
  },
};

export default MenuUtils;
